import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEnvelope, FaPhone, FaLinkedin, FaInstagram, FaFacebook, FaTwitter, FaMapMarkerAlt, FaExternalLinkAlt } from 'react-icons/fa';
import logo from "../assets/logo1.webp";

// All four Avani Enterprises office locations. Linking each to Google Maps
// + listing them in JSON-LD (see index.html) helps the local-pack ranking
// for "HRMS Gurgaon", "HR software Mumbai" etc.
const OFFICES = [
    {
        city: 'Gurgaon',
        address: 'Tower B, 3rd Floor, Unitech Cyber Park, Sector 39, 122002',
        full: 'Tower B, 3rd Floor, Unitech Cyber Park, Sector 39, Gurugram, Haryana 122002'
    },
    {
        city: 'Mumbai',
        address: 'Third Floor, Vasudev Chamber, Teli Galli Cross Rd, Andheri East, 400069',
        full: 'Third Floor, Vasudev Chamber, Teli Galli Cross Rd, Andheri East, Mumbai 400069'
    },
    {
        city: 'Rohtak',
        address: '106, First Floor, Agro Mall, Rohtak',
        full: '106, First Floor, Agro Mall, Rohtak, Haryana'
    },
    {
        city: 'Noida',
        address: 'Noida, Uttar Pradesh (Delhi NCR)',
        full: 'Noida, Uttar Pradesh'
    },
    {
        city: 'Australia',
        address: 'Australia',
        full: 'Australia'
    }
];

// Free public calculators / tools — surfaced in the footer for SEO internal linking.
const CALCULATORS = [
    { name: 'Salary Calculator', to: '/salary-calculator' },
    { name: 'CTC Calculator', to: '/ctc-calculator' },
    { name: 'PF Calculator', to: '/pf-calculator' },
    { name: 'ESI Calculator', to: '/esi-calculator' },
    { name: 'Gratuity Calculator', to: '/gratuity-calculator' },
    { name: 'TDS Calculator', to: '/tds-calculator' },
    { name: 'HRA Calculator', to: '/hra-calculator' },
    { name: 'Income Tax Calculator', to: '/income-tax-calculator-india' },
    { name: 'CTC to In-Hand', to: '/ctc-to-in-hand-calculator' },
    { name: 'Professional Tax', to: '/professional-tax-calculator' },
    { name: 'Bonus Calculator', to: '/bonus-calculator-india' },
    { name: 'Leave Encashment', to: '/leave-encashment-calculator' },
    { name: 'Overtime Calculator', to: '/overtime-calculator' },
    { name: 'Minimum Wage', to: '/minimum-wage-calculator-india' },
    { name: 'Salary Hike Calculator', to: '/salary-hike-calculator' },
    { name: 'Salary Slip Generator', to: '/salary-slip-generator' },
    { name: 'Rent Receipt Generator', to: '/rent-receipt-generator' },
    { name: 'Loan EMI Calculator', to: '/loan-emi-calculator' },
    { name: 'Balance Sheet Maker', to: '/balance-sheet-maker' },
    { name: 'P&L Statement Maker', to: '/profit-and-loss-statement' },
    { name: 'Salary Certificate Generator', to: '/salary-certificate-generator' },
    { name: 'Experience Letter Generator', to: '/experience-letter-generator' },
    { name: 'Full & Final Settlement', to: '/full-and-final-settlement-calculator' },
    { name: 'Offer Letter Generator', to: '/offer-letter-generator' },
    { name: 'Appointment Letter Generator', to: '/appointment-letter-generator' },
    { name: 'Increment Letter Generator', to: '/increment-letter-generator' },
    { name: 'Internship Certificate', to: '/internship-certificate-generator' },
    { name: 'Notice Period Calculator', to: '/notice-period-calculator' },
    { name: 'Leave Calculator', to: '/leave-calculator' },
    { name: 'Employee KPI Report Maker', to: '/employee-kpi-report-maker' },
];

// Avani Enterprises agency services — these point to the parent marketing
// website (avanienterprises.in). Descriptive anchor text + dofollow outbound
// links pass topical relevance/authority to the brand site for "website
// development", "Google Ads", "AI solutions" etc. Update a URL here once and it
// reflects on every page that renders the footer.
const SERVICES = [
  { name: 'Web & App Development', desc: 'Custom websites, apps & platforms', url: 'https://www.avanienterprises.in/services/web-app-development' },
  { name: 'SEO & Content Marketing', desc: 'Rank higher & convert more', url: 'https://www.avanienterprises.in/services/seo-content-marketing' },
  { name: 'Social Media Marketing', desc: 'Content, ads & audience growth', url: 'https://www.avanienterprises.in/services/social-media-marketing' },
  { name: 'AI Solutions', desc: 'Custom AI agents & automation', url: 'https://www.avanienterprises.in/services/ai-solutions' },
  { name: 'Email CRM', desc: 'Send bulk emails with analytics without getting blocked', url: 'https://www.avanienterprises.in/services/email-crm' },
  { name: 'Financial Consulting', desc: 'Accounting, tax & compliance', url: 'https://www.avanienterprises.in/services/financial-consulting' },
  { name: 'Business Consultation', desc: 'Strategy, growth & operations', url: 'https://www.avanienterprises.in/services/business-consultation' },
  { name: 'Business Loans', desc: 'Funding & working capital', url: 'https://www.avanienterprises.in/services/business-loans' },
  { name: 'Business Insurance', desc: 'Cover for your business & team', url: 'https://www.avanienterprises.in/services/business-insurance' },
];

const PHONE = '+91 9253625099';
const EMAIL = 'kp@avanienterprises.in';

// All 13 sister Avani products. Edit a url here once and it updates the
// footer everywhere the footer is rendered (every page through MainLayout).
const OUR_SOLUTIONS = [
  { id: '01', name: 'HR Portal',                  desc: 'Complete HR Management',                    url: 'https://gurukripahrportal.avanienterprises.in' },
  { id: '02', name: 'Project & Leads',            desc: 'Centralized work & leads',                  url: 'https://team-lead-32ha.vercel.app' },
  { id: '03', name: 'Sales Edge',                 desc: 'Enterprise CRM',                            url: 'https://crm-sales-portal.vercel.app' },
  { id: '04', name: 'Placement Management',       desc: 'Campus hiring portal',                      url: 'https://placement-management-system-80spgis9n.vercel.app' },
  { id: '05', name: 'School Management',          desc: 'Academic & logistics platform',             url: 'https://sms-frontend-steel-xi.vercel.app/' },
  { id: '06', name: 'Alumni Portal',              desc: 'Networking & mentorship',                   url: 'https://alumnexus-xi.vercel.app/' },
  { id: '07', name: 'MotoExpress',                desc: 'Smart bike buy/sell/rent',                  url: 'https://bike-service-website-gamma.vercel.app/' },
  { id: '08', name: 'AutoExpress',                desc: 'Smart car buy/sell/rent',                   url: 'https://car-service-website-tau.vercel.app/' },
  { id: '09', name: 'LMS',                        desc: 'Learning management system',                url: 'https://learning-management-system-rho-two.vercel.app/' },
  { id: '10', name: 'Millionaire Club',           desc: 'Real-estate 3D marketplace',                url: 'https://real-estate-platform-self.vercel.app' },
  { id: '11', name: 'College Management',         desc: 'Smart academic platform',                   url: 'https://cms-lovat-phi.vercel.app/' },
  { id: '12', name: 'GaonSeGharTak',              desc: 'Quick-commerce groceries',                  url: 'https://gaonseghartak.in' },
  { id: '13', name: 'Hospital Management',        desc: 'Patient & ward management',                 url: '#' },
  { id: '14', name: 'SocialSync',                 desc: 'Social media management platform',          url: 'https://socialsync.avanienterprises.in' },
  { id: '15', name: 'EaseGlow',                   desc: 'Beauty parlour & salon platform',           url: 'https://easeglow-beauty-client.vercel.app/' },
  { id: '16', name: 'Urban Co',                   desc: 'On-demand home services marketplace',       url: 'https://urban-company-seven.vercel.app/' },
  { id: '17', name: 'Red Ball Sports Arena',      desc: 'QR-based sports club entry',                url: 'https://redballsportsarena.com' },
  { id: '18', name: 'OwlStitch',                  desc: 'Women & girls clothing store',              url: 'https://owlstitch.com' }
];

export default function Footer() {
  const [showBigHeart, setShowBigHeart] = useState(false);

  // Default Google Maps link points at the head office (Gurgaon).
  const mapsUrl = (a) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a)}`;

  return (
    <motion.footer
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 1 }}
      viewport={{ once: true }}
      className="bg-[#433020] text-white py-12 relative shadow-lg rounded-t-3xl overflow-hidden font-sans"
    >
      {/* Fullscreen Heart Popup */}
      <AnimatePresence>
        {showBigHeart && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 12 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.6 }}
            className="fixed top-1/2 left-1/2 z-[9999] pointer-events-none"
            style={{ transform: 'translate(-50%, -50%)' }}
          >
            <div className="text-red-500 text-[10rem] select-none">❤️</div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10">

        {/* -- Column 1 (3/12): Branding, address, social -- */}
        <div className="lg:col-span-3 flex flex-col space-y-5">
          <Link to="/" className="flex items-center gap-3 hover:scale-105 transition w-fit">
            <img src={logo} alt="Avani Business OS Logo" width="200" height="200" className="h-16 w-auto" />
            <div className="flex flex-col">
              <span className="text-2xl font-bold tracking-wide leading-none">AVANI</span>
              <span className="text-lg font-medium tracking-wide text-[#fff5e6]">
                BUSINESS <span className="inline-block px-1.5 rounded bg-[#fff5e6] text-[#433020]">OS</span>
              </span>
            </div>
          </Link>

          <p className="text-white/80 text-sm italic max-w-xs">
            The complete operating system for modern businesses. HR, CRM, Finance, Projects & Chat — all in one workspace.
          </p>

          {/* Visit us — parent website link */}
          <a
            href="https://avanienterprises.in"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-white/90 hover:text-[#fff5e6] transition text-sm font-semibold w-fit"
          >
            <FaExternalLinkAlt size={11} />
            avanienterprises.in
          </a>

          {/* Phone + Email — direct contact channels */}
          <div className="space-y-2 text-sm">
            <a href={`tel:${PHONE.replace(/\s/g, '')}`} className="flex items-center gap-2.5 text-white/90 hover:text-[#fff5e6] transition group w-fit">
              <FaPhone className="text-[#fff5e6] group-hover:scale-110 transition shrink-0" size={14} />
              <span>{PHONE}</span>
            </a>
            <a href={`mailto:${EMAIL}`} className="flex items-center gap-2.5 text-white/90 hover:text-[#fff5e6] transition group w-fit">
              <FaEnvelope className="text-[#fff5e6] group-hover:scale-110 transition shrink-0" size={14} />
              <span className="break-all">{EMAIL}</span>
            </a>
          </div>

          {/* Social Icons */}
          <div className="flex items-center gap-4 mt-2">
            <SocialIcon href="https://www.instagram.com/avanienterprises.branding/" icon={<FaInstagram />} label="Follow Avani Enterprises on Instagram" />
            <SocialIcon href="https://www.facebook.com/people/Avani-Enterprises/61576229620845/?rdid=fw5wiWOMhyXHBVrN&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1DKFWQiBe4%2F" icon={<FaFacebook />} label="Follow Avani Enterprises on Facebook" />
            <SocialIcon href="https://x.com/avanienterprises" icon={<FaTwitter />} label="Follow Avani Enterprises on X (Twitter)" />
            <SocialIcon href="https://linkedin.com" icon={<FaLinkedin />} label="Connect with Avani Enterprises on LinkedIn" />
            <SocialIcon href={`mailto:${EMAIL}`} icon={<FaEnvelope />} label="Email Avani Enterprises" />
          </div>
        </div>

        {/* -- Column 2 (6/12): Full ecosystem (13 sister solutions) -- */}
        <div className="lg:col-span-6">
          <h1 className="text-3xl font-extrabold text-[#fff5e6] drop-shadow-sm mb-2">
            <span className="border-b-4 border-[#fff5e6] pb-1 rounded-sm">Our Ecosystem</span>
          </h1>
          <p className="text-white/60 text-sm mb-6">
            Eighteen production platforms — one company. Click any name to open the live portal.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {OUR_SOLUTIONS.map((s) => (
              <a
                key={s.id}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-2.5 p-2 -mx-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <span className="flex-shrink-0 w-7 h-7 rounded-md bg-[#fff5e6]/15 text-[#fff5e6] text-[10px] font-extrabold flex items-center justify-center mt-0.5">
                  {s.id}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-white group-hover:text-[#fff5e6] transition-colors flex items-center gap-1.5 leading-tight">
                    <span className="truncate">{s.name}</span>
                    <FaExternalLinkAlt className="flex-shrink-0 opacity-0 group-hover:opacity-70 transition-opacity" size={9} />
                  </div>
                  <div className="text-[11px] text-white/55 leading-snug mt-0.5">{s.desc}</div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* -- Column 3 (3/12): All four office locations -- */}
        <div className="lg:col-span-3 flex flex-col space-y-4">
          <h3 className="text-xl font-bold text-[#fff5e6] relative inline-block w-fit">
            Visit Us
            <span className="absolute -bottom-2 left-0 w-1/2 h-[2px] bg-[#fff5e6] rounded-full"></span>
          </h3>

          {/* Office grid — each block clickable into Google Maps. Helps with
              local-pack ranking for "HRMS Gurgaon", "HR software Mumbai" etc. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-2.5">
            {OFFICES.map((o) => (
              <a
                key={o.city}
                href={mapsUrl(o.full)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 p-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition group border border-white/5 hover:border-white/15"
              >
                <FaMapMarkerAlt className="mt-0.5 text-[#fff5e6] group-hover:scale-110 transition shrink-0" size={13} />
                <div className="min-w-0">
                  <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#fff5e6]">{o.city}</div>
                  <div className="text-[11px] text-white/75 leading-snug mt-0.5">{o.address}</div>
                </div>
              </a>
            ))}
          </div>

          {/* Compact map of the Gurgaon HQ — kept small so the office list
              gets visual priority and the footer doesn't get too tall. */}
          <div className="w-full h-32 rounded-xl overflow-hidden shadow-lg border-2 border-white/10 hover:border-[#8a6144]/50 transition duration-300">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3507.632289656461!2d77.0516686761066!3d28.46049889176121!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d1839e9f2913f%3A0xa199b90c29f2c3d5!2sUnitech%20Cyber%20Park!5e0!3m2!1sen!2sin!4v1709228892834!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Avani Enterprises HQ — Gurgaon"
              className="w-full h-full"
            ></iframe>
          </div>
        </div>

      </div>

      {/* Avani Enterprises services — outbound brand links (web/app/AI/ads/consulting) */}
      <div className="max-w-7xl mx-auto px-6 mt-10 pt-8 border-t border-white/10">
        <h3 className="text-xl font-bold text-[#fff5e6] mb-1">More from Avani Enterprises</h3>
        <p className="text-white/60 text-sm mb-5">
          Beyond Business OS — full-stack growth services for modern businesses.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {SERVICES.map((s) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener"
              title={`${s.name} — Avani Enterprises`}
              className="group flex items-start gap-2.5 p-2.5 -mx-2 rounded-lg hover:bg-white/5 transition-colors"
            >
              <FaExternalLinkAlt className="flex-shrink-0 mt-1 text-[#fff5e6]/70 group-hover:text-[#fff5e6] transition-colors" size={11} />
              <div className="min-w-0">
                <div className="text-sm font-bold text-white group-hover:text-[#fff5e6] transition-colors leading-tight">{s.name}</div>
                <div className="text-[11px] text-white/55 leading-snug mt-0.5">{s.desc}</div>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Free calculators & tools — internal links for SEO + visitor utility */}
      <div className="max-w-7xl mx-auto px-6 mt-10 pt-8 border-t border-white/10">
        <a href="https://play.google.com/store/apps/details?id=com.avanienterprises.Hr" target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-2 bg-[#fff5e6] hover:bg-white text-[#433020] font-extrabold text-xs px-4 py-2.5 rounded-full mb-5 transition-colors">
          &#128241; Download the Android app
        </a>
        <h3 className="text-xs font-extrabold uppercase tracking-widest text-[#fff5e6] mb-3">Free Calculators, Tools &amp; Blog</h3>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <Link to="/blog" className="text-[13px] text-[#fff5e6] font-bold hover:text-amber-300 transition-colors">
            Blog &amp; Insights
          </Link>
          <Link to="/free-tools" className="text-[13px] text-amber-300 font-bold hover:text-amber-200 transition-colors">
            All Free Tools &rarr;
          </Link>
          {CALCULATORS.map((c) => (
            <Link key={c.to} to={c.to} className="text-[13px] text-white/70 hover:text-[#fff5e6] transition-colors">
              {c.name}
            </Link>
          ))}
        </div>
      </div>

      {/* Bottom - Made with love line */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="text-center mt-12 pt-8 border-t border-white/10 text-sm text-white/50"
      >
        <div className="flex flex-col md:flex-row justify-center items-center gap-2">
          <p>© 2026 Avani Business OS — A product of Avani Enterprises. All rights reserved.</p>
          <span className="hidden md:block mx-2">|</span>
          <Link to="/privacy-policy" className="hover:text-white transition-colors duration-200">Privacy Policy</Link>
          <span className="hidden md:block mx-2">|</span>
          <motion.p
            onMouseEnter={() => setShowBigHeart(true)}
            onMouseLeave={() => setShowBigHeart(false)}
            whileHover={{ scale: 1.05 }}
            className="inline-block cursor-pointer flex items-center gap-1"
          >
            Made with <span className="animate-heartbeat text-red-500 text-lg">❤️</span> by <span className="text-[#fff5e6] font-semibold">Soham</span>
          </motion.p>
        </div>
      </motion.div>

      {/* Custom styles */}
      <style>{`
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.3); }
        }
        .animate-heartbeat {
          animation: heartbeat 1.8s infinite ease-in-out;
        }
      `}</style>
    </motion.footer>
  );
}

// Helper Component for Social Icons
const SocialIcon = ({ href, icon, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#8a6144] hover:text-white transition-all duration-300 hover:scale-110"
  >
    {icon}
  </a>
);
